<template>
  <v-app>
    <!-- Until the router has settled on the first page (it may still redirect to the login screen) show no
         chrome at all, so the menu button and logo do not flash up on a reload. -->
    <template v-if="!routerReady">
      <v-main></v-main>
    </template>
    <template v-else-if="isLoginRoute">
      <v-main>
        <router-view/>
      </v-main>
    </template>
    <template v-else>
      <v-app-bar style="background-color:#ffffff;border-bottom:1px solid var(--c-border);" elevation="0">
        <v-badge v-if="auth.isDeveloper && problems.open > 0" :content="problems.open" color="error" offset-x="6" offset-y="6">
          <v-app-bar-nav-icon data-guide="nav-menu" style="color:var(--c-text);" @click="drawerOpen = !drawerOpen"></v-app-bar-nav-icon>
        </v-badge>
        <v-app-bar-nav-icon v-else data-guide="nav-menu" style="color:var(--c-text);" @click="drawerOpen = !drawerOpen"></v-app-bar-nav-icon>
        <div class="d-flex flex-column justify-center" style="line-height:1.1;">
          <span style="color:var(--c-brand);font-weight:700;font-size:18px;">Common Sense</span>
          <span style="color:var(--c-muted);font-size:11px;">deur Linde Design (Pty) Ltd</span>
        </div>
        <v-spacer></v-spacer>
        <v-menu>
          <template v-slot:activator="{ props }">
            <img
              v-if="auth.logoUrl"
              v-bind="props"
              data-guide="user-menu"
              :src="auth.logoUrl"
              :alt="auth.brokerageName || 'Logo'"
              class="mr-3"
              style="height:36px;max-width:120px;object-fit:contain;cursor:pointer;"
            />
            <img
              v-else
              v-bind="props"
              data-guide="user-menu"
              :src="logo"
              alt="Common Sense"
              class="mr-3"
              style="height:36px;width:36px;object-fit:contain;cursor:pointer;"
            />
          </template>
          <v-list density="comfortable" nav>
            <v-list-item v-if="auth.firstName" :title="`Hallo, ${auth.firstName}`" class="text-caption" style="color:var(--c-muted);" disabled></v-list-item>
            <v-divider class="my-1"></v-divider>
            <v-list-item v-if="auth.brokerageCount > 1" title="Wissel makelaar" @click="navigate('/kies-makelaar')"></v-list-item>
            <v-list-item v-if="auth.isAdmin" title="Gebruikers" @click="navigate('/gebruikers')"></v-list-item>
            <v-list-item v-if="auth.isAdmin" title="Instellings" @click="navigate('/instellings')"></v-list-item>
            <v-list-item v-if="auth.isAdmin && auth.billingEnabled" title="Intekening" @click="navigate('/abonnement')"></v-list-item>
            <v-list-item title="Verander wagwoord" @click="auth.passwordDialogOpen = true"></v-list-item>
            <v-list-item title="Teken uit" @click="logout"></v-list-item>
          </v-list>
        </v-menu>
      </v-app-bar>
      <v-navigation-drawer v-model="drawerOpen" temporary>
        <v-list density="comfortable" nav>
          <v-list-item v-for="link in navLinks" :key="link.to" :data-guide="link.guide" :title="link.label" @click="navigate(link.to)"></v-list-item>
          <v-list-group value="nakoming">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" title="Nakoming"></v-list-item>
            </template>
            <v-list-item
              v-for="link in complianceLinks"
              :key="link.to"
              :title="link.label"
              @click="navigate(link.to)"
            ></v-list-item>
          </v-list-group>
          <v-list-group v-if="auth.isDeveloper" value="ontwikkelaar">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" title="Ontwikkelaar">
                <template v-slot:append>
                  <v-badge v-if="problems.open > 0" :content="problems.open" color="error" inline></v-badge>
                </template>
              </v-list-item>
            </template>
            <v-list-item
              v-for="link in developerLinks.filter((l) => !l.billing || auth.billingEnabled)"
              :key="link.to"
              :title="link.label"
              @click="navigate(link.to)"
            >
              <template v-if="link.to === '/skedule-probleme' && problems.open > 0" v-slot:append>
                <v-badge :content="problems.open" color="error" inline></v-badge>
              </template>
            </v-list-item>
          </v-list-group>
        </v-list>
      </v-navigation-drawer>

      <v-main>
        <PasswordChangeDialog />
        <router-view/>
      </v-main>
      <DuckMount />

    </template>
  </v-app>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from './stores/auth';
import { useScheduleProblemsStore } from './stores/scheduleProblems';
import logo from '@/assets/common-sense-logo.png';
import PasswordChangeDialog from '@/components/PasswordChangeDialog.vue';
import DuckMount from '@/duck/DuckMount.vue';

export default defineComponent({
  name: 'App',
  components: { PasswordChangeDialog, DuckMount },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const routerReady = ref(false);
    router.isReady().then(() => { routerReady.value = true; });
    const auth = useAuthStore();
    const problems = useScheduleProblemsStore();

    // The developer is told about unreadable schedules whichever brokerage they are in: poll the cheap count.
    let problemTimer: number | undefined;
    watch(() => auth.isDeveloper && !!auth.token, (active) => {
      window.clearInterval(problemTimer);
      if (active) {
        problems.refresh();
        problemTimer = window.setInterval(() => problems.refresh(), 60000);
      } else {
        problems.open = 0;
      }
    }, { immediate: true });
    watch(() => route.fullPath, () => { if (auth.isDeveloper && auth.token) problems.refresh(); });

    // The login screen and the client's public signing page show no app chrome.
    const isLoginRoute = computed(() => route.name === 'login' || route.name === 'teken' || route.name === 'inskrywing-klaar'
      || route.name === 'vergeet-wagwoord' || route.name === 'herstel-wagwoord');
    const drawerOpen = ref(false);

    // Brokerage name / switcher / developer menu come from the server; skip while a brokerage still has to be chosen.
    watch(() => [auth.token, auth.needsBrokerage], () => {
      if (auth.token && !auth.needsBrokerage) {
        auth.loadSession().catch(() => undefined);
      }
    }, { immediate: true });

    const navLinks = computed(() => {
      const links = [
        { to: '/', label: 'Oorsig', guide: 'nav-oorsig' },
        { to: '/details', label: 'Skedule Analise', guide: 'nav-details' },
        { to: '/clients', label: 'Kliënte', guide: 'nav-clients' },
        { to: '/wysigings', label: 'Wysigings', guide: 'nav-wysigings' },
        { to: '/eise', label: 'Eise', guide: 'nav-eise' },
        { to: '/kwotasies', label: 'Kwotasies', guide: 'nav-kwotasies' },
      ];
      return links;
    });

    // "Nakoming" = compliance - these registers exist for audit purposes
    // (see spec), grouped separately from day-to-day work screens above.
    const complianceLinks = [
      { to: '/registers/kansellasies', label: 'Register: Kansellasies' },
      { to: '/registers/nuwe-aansoeke', label: 'Register: Nuwe aansoeke' },
      { to: '/registers/rekords-van-advies', label: 'Register: Rekords van Advies' },
      { to: '/rekords-nodig', label: 'Polisse sonder Rekord van Advies' },
    ];

    const developerLinks = [
      { to: '/makelaars', label: 'Makelaars' },
      { to: '/parser-terugvoer', label: 'Parser terugvoer' },
      { to: '/skedule-probleme', label: 'Onleesbare skedules' },
      { to: '/duck-verslae', label: 'Eendjie-verslae' },
      { to: '/verkope', label: 'Verkope' },
      { to: '/intekenare', label: 'Intekenare en tariewe', billing: true },
    ];

    const navigate = (to: string) => {
      drawerOpen.value = false;
      router.push(to);
    };

    const logout = async () => {
      drawerOpen.value = false;
      await auth.logout();
      router.push({ name: 'login' });
    };

    return {
      auth,
      problems,
      logo,
      isLoginRoute,
      routerReady,
      drawerOpen,
      navLinks,
      complianceLinks,
      developerLinks,
      navigate,
      logout,
    };
  },
})
</script>
